"use client"

import { useEffect, useState, useRef } from 'react'

interface HeadingItem {
  id: string
  label: string
}

export default function TableOfContents() {
  const [headings, setHeadings] = useState<HeadingItem[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    // 1. Find all section titles in the DOM
    const elements = Array.from(document.querySelectorAll('.case-study-section-title')) as HTMLElement[]
    
    // 2. Generate IDs for elements that don't have them and build the headings array
    const newHeadings = elements.map((el, index) => {
      let id = el.id
      const label = el.textContent || `Section ${index + 1}`
      
      if (!id) {
        // Generate a URL-friendly ID from text, or use index fallback
        id = label
          .toLowerCase()
          .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-') // Allow alphanumeric and Chinese chars
          .replace(/(^-|-$)/g, '') || `section-${index}`
        
        // Ensure uniqueness if same name exists
        if (document.getElementById(id)) {
          id = `${id}-${index}`
        }
        el.id = id
      }
      return { id, label }
    })
    
    setHeadings(newHeadings)

    // 3. Setup IntersectionObserver
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    // Use a negative top margin to trigger when heading reaches near the top of viewport (e.g. 100px from top)
    const options = {
      rootMargin: '-100px 0px -60% 0px',
      threshold: 0
    }

    // Keep track of which headers are currently intersecting
    const visibleHeadings = new Set<string>()

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleHeadings.add(entry.target.id)
        } else {
          visibleHeadings.delete(entry.target.id)
        }
      })

      // If multiple headings are visible, set the active one to the first in DOM order
      if (visibleHeadings.size > 0) {
        // newHeadings maintains DOM order
        const firstVisible = newHeadings.find((h) => visibleHeadings.has(h.id))
        if (firstVisible) {
          setActiveId(firstVisible.id)
        }
      }
    }, options)

    elements.forEach((el) => {
      if (observerRef.current) observerRef.current.observe(el)
    })

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, []) // Empty dependency array, runs once after initial render of the case study

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const targetElement = document.getElementById(id)
    if (!targetElement) return

    // Calculate offset considering the fixed navbar (e.g., 80px) + some padding (20px)
    const headerOffset = 100 
    const elementPosition = targetElement.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.scrollY - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }

  if (headings.length === 0) {
    return null
  }

  return (
    <nav className="toc-sidebar">
      <ul className="toc-list">
        {headings.map((heading) => (
          <li key={heading.id} className="toc-item-wrapper">
            <a
              href={`#${heading.id}`}
              onClick={(e) => handleSmoothScroll(e, heading.id)}
              className={`toc-item ${activeId === heading.id ? 'active' : ''}`}
            >
              {heading.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
